import React, { useEffect, useState, useCallback } from 'react';
import { CalendarDays, CalendarRange, ChevronLeft, ChevronRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import ManagePlanning from './ManagePlanning';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_LABELS = { Mon: 'Lun', Tue: 'Mar', Wed: 'Mer', Thu: 'Jeu', Fri: 'Ven', Sat: 'Sam', Sun: 'Dim' };

// Vue MOIS : la grille récurrente projetée sur chaque date + renforts « dimanche du mois ».
// (La partie « Proposer mes disponibilités » a été RETIRÉE le 02/09 à la demande de Marlon :
// le planning est une grille fixe, seul le choix du dimanche reste, géré par son popup.)
function MonthView({ canManage, firstName }) {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [data, setData] = useState(null);
  const load = useCallback((m) => {
    api.get('/planning/month', { params: { month: m } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e, 'Échec vue mois')));
  }, []);
  useEffect(() => { load(month); }, [month, load]);

  const shift = (delta) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const label = (() => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  })();

  const jours = data?.jours || [];
  // Cale la 1re semaine : le mois peut commencer un mercredi → cases vides avant.
  const pad = jours.length ? DAYS.indexOf(jours[0].jour) : 0;
  const cells = [...Array(pad).fill(null), ...jours];
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
      <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
        <CalendarRange size={15} className="text-indigo-400" />
        <span className="text-sm font-semibold text-white">Vue mois</span>
        <span className="text-[11px] text-slate-500">· grille de la semaine projetée + renforts dimanche</span>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => shift(-1)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white"><ChevronLeft size={15} /></button>
          <span className="text-sm text-white font-medium capitalize min-w-[130px] text-center">{label}</span>
          <button onClick={() => shift(1)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white"><ChevronRight size={15} /></button>
        </div>
      </div>
      <div className="overflow-x-auto p-3">
        <div className="grid grid-cols-7 gap-1.5 min-w-[860px]">
          {DAYS.map(d => (
            <div key={d} className="text-center text-[11px] uppercase tracking-wider text-slate-500 font-semibold py-1">{DAY_LABELS[d]}</div>
          ))}
          {cells.map((j, i) => j === null
            ? <div key={`pad-${i}`} />
            : (
              <div key={j.date} className={`rounded-xl border p-1.5 min-h-[92px] ${j.date === todayIso ? 'border-indigo-400/50 bg-indigo-500/[0.06]' : 'border-white/10 bg-white/[0.02]'}`}>
                <div className={`text-[11px] font-bold mb-1 ${j.date === todayIso ? 'text-indigo-300' : 'text-slate-400'}`}>{Number(j.date.slice(8, 10))}</div>
                <div className="space-y-0.5">
                  {(j.slots || []).map((s, k) => (
                    <div key={k} className="text-[10px] leading-tight text-slate-300">
                      <span className="text-slate-500">{s.shift} : </span>
                      {(s.chatters || []).map((n) => {
                        const isMe = (n || '').split(' ')[0] === firstName;
                        return <span key={n} className={isMe && !canManage ? 'text-indigo-300 font-semibold' : ''}>{n}{'  '}</span>;
                      })}
                    </div>
                  ))}
                  {(j.renforts || []).map((r, k) => (
                    <div key={`r-${k}`} className="text-[10px] leading-tight text-amber-300">☀️ {r.chatter} ({r.moment})</div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

export default function Planning() {
  const { user, hasPerm } = useAuth();
  const canManage = hasPerm('planning_manage');
  const [view, setView] = useState('mine');
  const [shifts, setShifts] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const load = async () => {
    try {
      const [s, a] = await Promise.all([api.get('/planning/shifts'), api.get('/planning/assignments', { params: { week: 'current' } })]);
      setShifts(s.data);
      setAssignments(a.data);
    } catch (e) { toast.error(apiError(e, 'Failed to load planning')); }
  };
  useEffect(() => { load(); }, []);

  const cell = (day, shiftId) => assignments.find(x => x.day === day && x.shift_id === shiftId);
  const firstName = (user?.name || '').split(' ')[0];

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="planning-page">
      <PageHeader title="Planning" subtitle={view === 'manage' ? 'Gérer les créneaux et les affectations' : 'Ta semaine type + la vue mois (renforts dimanche inclus)'} onRefresh={view === 'mine' ? load : undefined} />

      {canManage && (
        <div className="inline-flex rounded-xl border border-white/10 bg-white/[0.03] p-1 mb-4" data-testid="planning-view-tabs">
          <button onClick={() => setView('mine')} data-testid="planning-tab-mine"
            className={`px-4 py-1.5 text-sm rounded-lg flex items-center gap-1.5 transition-all ${view === 'mine' ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/30' : 'text-slate-400 hover:text-white'}`}>
            <CalendarDays size={14} /> Mon planning
          </button>
          <button onClick={() => setView('manage')} data-testid="planning-tab-manage"
            className={`px-4 py-1.5 text-sm rounded-lg flex items-center gap-1.5 transition-all ${view === 'manage' ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/30' : 'text-slate-400 hover:text-white'}`}>
            <CalendarRange size={14} /> Gérer
          </button>
        </div>
      )}

      {view === 'manage' && canManage ? <ManagePlanning embedded /> : (
      <>
      {assignments.length === 0 && (
        <div className="rounded-xl border border-indigo-500/15 bg-indigo-500/[0.04] px-4 py-3 mb-4 text-[13px] text-slate-300 flex items-center gap-2">
          <CalendarDays size={16} className="text-indigo-400 shrink-0" /> Ton planning s'affichera ici dès que des créneaux te seront assignés.
        </div>
      )}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden mb-8">
        <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
          <CalendarDays size={15} className="text-indigo-400" />
          <span className="text-sm font-semibold text-white">Semaine type</span>
          <span className="text-[11px] text-slate-500">· identique chaque semaine</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="px-4 py-2.5 text-left font-semibold">Shift</th>
                {DAYS.map(d => <th key={d} className="px-3 py-3.5 font-semibold text-center text-[13px]">{DAY_LABELS[d]}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {shifts.map(s => (
                <tr key={s.id}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
                      <div>
                        <div className="text-sm text-white font-medium">{s.label}</div>
                        <div className="text-[10px] text-slate-500">{s.time}</div>
                      </div>
                    </div>
                  </td>
                  {DAYS.map(d => {
                    const a = cell(d, s.id);
                    const names = (a?.chatter_names || []).filter(n => canManage || n.split(' ')[0] === firstName);
                    return (
                      <td key={d} className="px-2 py-2 text-center align-top">
                        <div className="space-y-1 min-h-[54px] flex flex-col justify-center">
                          {names.map(n => {
                            const isMe = (n || '').split(' ')[0] === firstName;
                            return (
                              <div key={n} className={`text-[12px] px-2 py-1.5 rounded-lg font-medium ${isMe ? `${s.color} text-white shadow-sm` : 'bg-white/5 border border-white/10 text-slate-300'}`}>{n}</div>
                            );
                          })}
                          {names.length === 0 && <span className="text-slate-700 text-base leading-none">·</span>}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <MonthView canManage={canManage} firstName={firstName} />
      </>
      )}
    </div>
  );
}
